import {CartLinesUpdateEvent, StandardEvents} from '@shopify/events';
import {
  PROMO_KEY,
  buildEligibilitySignature,
  createSingleUseTrigger,
  hasReachedPopupScroll,
  isCheckoutIntent,
  isPopupFrequencyBlocked,
  planGiftReconciliation,
  popupDelayMilliseconds,
  popupScrollMetrics,
  promotionEventsForPlanAction,
  shouldSuppressPopup,
} from './free-insole-promotion-core.js?v=20260831-4';

const configElement = document.getElementById('FreeInsolePromotionConfig');

if (configElement) {
  const config = JSON.parse(configElement.textContent || '{}');
  const eligibleProductIds = new Set((config.eligibleProductIds || []).map(Number));
  const suppressionKey = `safestep:${PROMO_KEY}:suppressed`;
  const pendingSuppression = '__pending_gift_removal__';
  const popupFrequencyKey = `safestep:${PROMO_KEY}:popup-seen-at`;
  const popupSessionKey = `safestep:${PROMO_KEY}:popup-seen-session`;
  const root = window.Shopify?.routes?.root || '/';
  let reconcileTimer = 0;
  let reconciling = false;
  let manualGiftRemovalPending = false;
  let lastGiftKeys = [];
  let lastEligibilitySignature = '';
  let checkoutEventSent = false;

  const statusElement = document.querySelector('[data-free-insole-status]');

  function cartUrl(path) {
    return `${root}${path}`;
  }

  async function request(path, options = {}) {
    const response = await fetch(cartUrl(path), {
      credentials: 'same-origin',
      headers: {Accept: 'application/json', ...(options.headers || {})},
      ...options,
    });
    const payload = await response.json();
    if (!response.ok || payload?.status) {
      throw new Error(payload?.description || payload?.message || `Cart request failed (${response.status})`);
    }
    return payload;
  }

  function getCart() {
    return request('cart.js');
  }

  function lineSize(item) {
    const option = item.options_with_values?.find(({name}) => String(name).toLowerCase() === 'size');
    return option?.value || null;
  }

  function toStateLines(cart) {
    return (cart.items || []).map((item) => ({
      key: item.key,
      variantId: item.variant_id,
      productId: item.product_id,
      size: lineSize(item),
      eligible: eligibleProductIds.has(Number(item.product_id)),
      isGift: item.properties?._promo_gift === PROMO_KEY,
      promoKey: item.properties?._promo_gift,
      sourceSize: item.properties?._source_size,
      quantity: item.quantity,
    }));
  }

  function setStatus(message = '', visible = false) {
    if (!statusElement) return;
    statusElement.textContent = message;
    statusElement.toggleAttribute('data-visible', Boolean(message && visible));
  }

  function emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, {detail}));
  }

  async function removeGiftLines(keys) {
    if (!keys.length) return;
    const updates = Object.fromEntries(keys.map((key) => [key, 0]));
    await request('cart/update.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({updates}),
    });
  }

  async function addGift(variantId, sourceSize) {
    await request('cart/add.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: {_promo_gift: PROMO_KEY, _source_size: sourceSize},
        }],
      }),
    });
  }

  function announceCart(cart, action = 'update') {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: (cart.items || []).map((item) => ({id: item.key, quantity: item.quantity})),
      promise: deferred.promise,
      detail: {source: 'free-insole-coordinator'},
    }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {source: 'free-insole-coordinator', items: cart.items},
    });
  }

  async function reconcile() {
    if (reconciling || config.promotionEnabled !== true) return;
    reconciling = true;

    try {
      const cart = await getCart();
      const lines = toStateLines(cart);
      const currentSignature = buildEligibilitySignature(lines);
      let storedSuppression = sessionStorage.getItem(suppressionKey) || '';

      if (storedSuppression === pendingSuppression) {
        storedSuppression = currentSignature;
        if (currentSignature) sessionStorage.setItem(suppressionKey, currentSignature);
      } else if (storedSuppression && storedSuppression !== currentSignature) {
        sessionStorage.removeItem(suppressionKey);
        storedSuppression = '';
      }

      const plan = planGiftReconciliation({
        lines,
        variants: config.giftVariants || {},
        suppressedSignature: storedSuppression,
      });

      lastGiftKeys = lines.filter((line) => line.isGift).map((line) => line.key);
      lastEligibilitySignature = currentSignature;

      if (plan.action === 'unmapped') {
        if (plan.giftLineKeys.length) {
          await removeGiftLines(plan.giftLineKeys);
          announceCart(await getCart(), 'remove');
        }
        setStatus(`Free insole size could not be matched for ${plan.sourceSize}.`, true);
        emit('free_insole_mapping_error', {size: plan.sourceSize});
        return;
      }

      if (plan.action === 'remove') {
        await removeGiftLines(plan.giftLineKeys);
        const updated = await getCart();
        announceCart(updated, 'remove');
        emit('free_insole_removed', {reason: 'no_eligible_footwear'});
      } else if (plan.action === 'replace') {
        await removeGiftLines(plan.giftLineKeys);
        await addGift(plan.variantId, plan.sourceSize);
        const updated = await getCart();
        announceCart(updated, 'update');
        emit('free_insole_reconciled', {action: 'replace', size: plan.sourceSize, variantId: plan.variantId});
      } else if (plan.action === 'add') {
        await addGift(plan.variantId, plan.sourceSize);
        const updated = await getCart();
        announceCart(updated, 'add');
        emit('free_insole_reconciled', {action: 'add', size: plan.sourceSize, variantId: plan.variantId});
        for (const eventName of promotionEventsForPlanAction(plan.action)) {
          emit(eventName, {size: plan.sourceSize, variantId: plan.variantId});
        }
      }

      if (plan.action !== 'unmapped') setStatus('');
    } catch (error) {
      console.warn('[free-insole] Reconciliation failed:', error);
      setStatus('Your free insole could not be updated. Please refresh the cart or contact support.', true);
      emit('free_insole_error', {message: error instanceof Error ? error.message : String(error)});
    } finally {
      reconciling = false;
    }
  }

  function scheduleReconcile(delay = 80) {
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(reconcile, delay);
  }

  function onCartLinesUpdate(event) {
    if (event.detail?.source === 'free-insole-coordinator') return;

    if (manualGiftRemovalPending) {
      Promise.resolve(event.promise).catch(() => {}).finally(async () => {
        try {
          const updated = await getCart();
          const signature = buildEligibilitySignature(toStateLines(updated));
          if (signature) sessionStorage.setItem(suppressionKey, signature);
          lastEligibilitySignature = signature;
        } catch {
          sessionStorage.setItem(suppressionKey, pendingSuppression);
        } finally {
          manualGiftRemovalPending = false;
        }
      });
      return;
    }

    if (event.action === 'remove') {
      const removedKeys = (event.lines || []).map((line) => String(line.id));
      if (lastGiftKeys.some((key) => removedKeys.includes(String(key))) && lastEligibilitySignature) {
        sessionStorage.setItem(suppressionKey, lastEligibilitySignature);
        emit('free_insole_manual_remove', {signature: lastEligibilitySignature});
      }
    }

    Promise.resolve(event.promise).catch(() => {}).finally(() => scheduleReconcile());
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('a, button, input');
    const form = control?.closest('form');
    if (!checkoutEventSent && isCheckoutIntent({
      href: control?.getAttribute('href') || '',
      formAction: control?.getAttribute('formaction') || form?.getAttribute('action') || '',
      name: control?.getAttribute('name') || '',
    })) {
      checkoutEventSent = true;
      emit('free_insole_checkout_reached');
    }
    if (event.target.closest('[data-free-insole-remove-gift]')) {
      const signature = lastEligibilitySignature || pendingSuppression;
      manualGiftRemovalPending = true;
      sessionStorage.setItem(suppressionKey, signature);
      emit('free_insole_manual_remove', {signature});
      return;
    }
    if (event.target.closest('[data-free-insole-restore]')) {
      sessionStorage.removeItem(suppressionKey);
      scheduleReconcile(0);
    }
  }, {capture: true});
  document.addEventListener('submit', (event) => {
    if (checkoutEventSent || !(event.target instanceof HTMLFormElement)) return;
    if (isCheckoutIntent({formAction: event.target.getAttribute('action') || ''})) {
      checkoutEventSent = true;
      emit('free_insole_checkout_reached');
    }
  }, {capture: true});

  if (Number(config.cartItemCount) > 0) scheduleReconcile(0);

  function initializePopup() {
    const dialog = document.querySelector('[data-free-insole-popup]');
    const lastSeen = Number(localStorage.getItem(popupFrequencyKey) || 0);
    const popupContext = {
      promotionEnabled: config.promotionEnabled,
      popupEnabled: config.popupEnabled,
      templateName: config.templateName,
      pathName: window.location.pathname,
      sessionSeen: Boolean(sessionStorage.getItem(popupSessionKey)),
      frequencyBlocked: isPopupFrequencyBlocked({lastSeenAt: lastSeen, frequencyDays: config.popupFrequencyDays || 7}),
    };
    if (!(dialog instanceof HTMLDialogElement) || shouldSuppressPopup(popupContext)) return;

    const pageWrapper = document.querySelector('.page-wrapper');
    const scrollingElement = document.scrollingElement || document.documentElement;
    const initialScrollMetrics = popupScrollMetrics({
      pageWrapper,
      scrollingElement,
      windowScrollY: window.scrollY,
      windowInnerHeight: window.innerHeight,
    });
    const scrollTarget = initialScrollMetrics.source === 'page-wrapper' ? pageWrapper : window;
    const claimTrigger = createSingleUseTrigger();
    let previousFocus = null;
    const image = dialog.querySelector('[data-popup-image]');

    function rememberSeen() {
      localStorage.setItem(popupFrequencyKey, String(Date.now()));
      sessionStorage.setItem(popupSessionKey, '1');
    }

    function close(reason) {
      rememberSeen();
      dialog.close();
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
      emit(reason === 'cta' ? 'free_insole_popup_cta' : 'free_insole_popup_close', {reason});
    }

    async function open() {
      if (!claimTrigger()) return;
      scrollTarget.removeEventListener('scroll', onScroll);

      try {
        const cart = Number(config.cartItemCount) > 0 ? await getCart() : {items: []};
        const lines = toStateLines(cart);
        if (shouldSuppressPopup({...popupContext, cartLines: lines})) return;
      } catch {
        return;
      }

      if (image instanceof HTMLImageElement && image.dataset.src) {
        image.src = image.dataset.src;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      }

      previousFocus = document.activeElement;
      dialog.showModal();
      rememberSeen();
      emit('free_insole_popup_view');
    }

    function onScroll() {
      const metrics = popupScrollMetrics({
        pageWrapper,
        scrollingElement,
        windowScrollY: window.scrollY,
        windowInnerHeight: window.innerHeight,
      });
      if (hasReachedPopupScroll({
        scrollY: metrics.scrollTop,
        scrollHeight: metrics.scrollHeight,
        viewportHeight: metrics.clientHeight,
        thresholdPercent: config.popupScrollPercent || 40,
      })) open();
    }

    window.setTimeout(open, popupDelayMilliseconds(config.popupDelaySeconds || 25));
    scrollTarget.addEventListener('scroll', onScroll, {passive: true});

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close('escape');
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || (event.target instanceof Element && event.target.closest('[data-popup-close]'))) {
        close('dismiss');
      }
    });
    dialog.querySelector('[data-popup-cta]')?.addEventListener('click', (event) => {
      if (config.currentProductEligible === true) {
        event.preventDefault();
        close('cta');
        document.querySelector('product-form-component')?.scrollIntoView({behavior: 'smooth', block: 'center'});
      } else {
        rememberSeen();
        emit('free_insole_popup_cta', {destination: config.popupDestination});
      }
    });
  }

  initializePopup();
}
